<template>
    <div class="" style="min-width:900px">
        <h4 class="h h-sm u-mrg-top-none u-mrg-btm-qtr">Your Saved Projects</h4>
        <div class="form-element form-element__search project-search u-mrg-btm-half">
            <label for="project-search" class="visuallyhidden">Project Search</label>
            <div class="btn--search btn--search-alt u-padd-left-qtr">
                <span class="visuallyhidden">Perform</span>
                <svg class="icon" preserveAspectRatio="xMidYMid meet" role="img" viewBox="0 0 32 32" aria-label=" icon">
                    <use xlink:href="#MAGNIFYING_GLASS" />
                </svg>
            </div>
            <input aria-label="Project Search" class="input input--search-project" id="project-search"
                placeholder="Search by Site Name" role="search" type="search" v-model="searchTerm" />
        </div>

        <EasyDataTable :headers="headers" :items="items" :search-value="searchTerm" :rows-per-page="pageSize"
            :rows-per-page-options="pageSizes" :loading="loading" :sort-by="sortBy" :sort-type="sortType"
            :must-sort="mustSort" header-text-direction="left" body-text-direction="left"
            table-class-name="saved-projects-table" theme-color="#73A300"
            @update-page-items="updatePageItems"
            >
            <template #item-date="{ date }">
                <span class="cell-date">{{ formatDate(date) }}</span>
            </template>
            <template #item-status="{ status }">
                <span>
                    <template v-if="status && status.includes('Done')">
                        <i class="fa fa-check-square status-done"></i>
                    </template>
                    <template v-else-if="status && status.includes('Error')">
                        <i class="fa fa-exclamation-triangle status-error"></i>
                    </template>
                    <template v-else-if="status && status !== '' && !status.includes('Unsubmitted')">
                        <i class="fa fa-cog status-generic"></i>
                    </template>
                    {{ status }}
                </span>
            </template>
            <template #item-action="item">
                <a :href="`${item.urlName}?quoteId=${item.sfId}`" :data-quote="item.id"
                    class="quote-open link-cta u-cursor-p">
                    View/Edit
                </a>
                &nbsp;
                <div :data-quote="item.id" title="Save a copy of this project to a new project" aria-role="button" @click="clone(item.sfId)"
                    class="quote-open link-cta u-cursor-p">
                    Clone
                </div>
            </template>
            <template #empty-message>
                <div class="vue3-easy-data-table__message">
                    No projects found
                </div>
            </template>
        </EasyDataTable>
    </div>

    <!-- Clone Confirmation Dialog -->
    <CloneProjectDialog 
        :project="selectedProject"
        :visible="showCloneDialog"
        :clone-url="cloneQuoteUrl"
        @close="handleCloneDialogClose"
        @clone-success="handleCloneSuccess"
        @clone-error="handleCloneError"
    />
</template>

<script setup>
import { ref, onMounted } from 'vue'
import axios from "axios"
import EasyDataTable from 'vue3-easy-data-table'
import 'vue3-easy-data-table/dist/style.css'
import CloneProjectDialog from './CloneProjectDialog.vue'

const props = defineProps({
    dataUrl: {
        type:String,
        default: '/site-api/etools/quotes/user'
    },
    quoteStatusUrl: {
        type: String,
        default: '/site-api/etools/quotes/status'
    },
    cloneQuoteUrl:{
        type:String,
        default:"/site-api/etools/quotes/clone"
    }
})

const loading = ref(true)
const items = ref([])
const searchTerm = ref('')
const pageSize = ref(25)
const pageSizes = [25, 50, 75, 100]

const sortBy = 'date'
const sortType = 'desc'
const mustSort = true

const visibleItems = ref([])

// Dialog-related variables
const selectedProject = ref(null)
const showCloneDialog = ref(false)

const headers = [
    { text: 'Date', value: 'date', sortable: true, width: 100 },
    { text: 'Project ID', value: 'id', sortable: true, width: 100 },
    { text: 'Tool Name', value: 'toolName', sortable: true, width: 120 },
    { text: 'Site Name', value: 'siteName', sortable: true },
    { text: 'Status', value: 'status', sortable: false, width: 190 },
    { text: 'Actions', value: 'action', sortable: false, width: 150 }
]

let intervalId = null

function clone(sfId){
    // Find the project with the matching sfId
    const project = items.value.find(item => item.sfId === sfId)
    if (project) {
        selectedProject.value = project
        showCloneDialog.value = true
    }
}

// Dialog event handlers
function handleCloneDialogClose() {
    selectedProject.value = null
    showCloneDialog.value = false
}

function handleCloneSuccess(data) {
    console.log("Clone successful:", data)
    // Reload projects to show the new cloned project
    loadProjects()
    // No need to close it here, let user see the message and choose to close
}

function handleCloneError(data) {
    console.error("Clone failed:", data)
    // The dialog will handle showing the error state
    // No need to close it here, let user see the error and choose to retry or close
}

function applyStatusResults(results) {
    items.value.forEach(item => {
        if (results && results[item.id] !== undefined) {
            item.status = results[item.id]
        }
    })
}

function updatePageItems(val) {
    visibleItems.value = val
    // Only include items where status is null or empty string
    const itemsToUpdate = val.filter(item => !item.status);
    if (itemsToUpdate.length === 0) {
        // No items need status update, skip service call
        return;
    }
    const ids = itemsToUpdate.map(item => item.id);
    axios.post(props.quoteStatusUrl, { quotes: ids })
        .then(response => {
            applyStatusResults(response.data.result)
        })
        .catch(error => {
            console.error('Error fetching quote status:', error)
        })
}

function updateVisibleStatuses() {
    console.log("Updating visible statuses...")
    // Only include items where status is null, empty, and does not contain "Done" or "Error"
    const itemsToUpdate = visibleItems.value.filter(item => {
        const status = item.status
        return (
            status === null ||
            status === undefined ||
            (typeof status === 'string' && status.trim() === '') ||
            (
                typeof status === 'string' &&
                !status.includes('Done') &&
                !status.includes('Error') &&
                !status.includes("Unsubmitted")
            )
        )
    });
    if (itemsToUpdate.length === 0) {
        return;
    }
    const ids = itemsToUpdate.map(item => item.id);
    axios.post(props.quoteStatusUrl, { quotes: ids })
        .then(response => {
            applyStatusResults(response.data.result)
            console.log("Pulled remote status for: ", response.data.result)
        })
        .catch(error => {
            console.error('Error fetching remote status:', error)
            if (intervalId) {
                clearInterval(intervalId)
                intervalId = null
                console.log("Polling stopped due to error.")
            }
        })
}

function formatDate(value) {
    const date = new Date(value)
    return `${date.getMonth() + 1}/${date.getDate()}/${date.getFullYear()}`
}

async function loadProjects() {
    loading.value = true
    try {
        const response = await axios.get(props.dataUrl)
        items.value = response.data.result
    } catch (e) {
        items.value = []
    }
    loading.value = false
}

onMounted(async () => {
    await loadProjects()

    intervalId = setInterval(updateVisibleStatuses, 10000)
    setTimeout(() => {
        if (intervalId) {
            clearInterval(intervalId)
            intervalId = null
        }
    }, 7200000)
})
</script>

<style scoped lang="scss">
@use "@styles/vars/_units.scss" as *;
@use "@styles/vars/_colors" as *;

:deep(span.header) {
    all: unset;
}

.saved-projects-table {
    --easy-table-border: 0;
    --easy-table-body-row-font-size: #{$sm-font-size};
}

.cell-date {
    font-weight: 700;
}

.status-done {
    color: $color-green-md;
    margin-right: 0.25em;
}
.status-error {
    color: $color-error;
    margin-right: 0.25em;
}
.status-generic {
    color: $color-gray;
    margin-right: 0.25em;
}
</style>
